/**
 * Magic-link verification callback (P1-T11): `#/auth/verify?token=…`.
 * Verifies the token, stores the session, then lands on Home.
 * Public route — no top bar or bottom nav.
 */
import { ApiError, api, loadTokens } from '../api/client.js';
import { setUserAfterSignIn } from '../state/auth.js';
import { attributionPayload } from '../marketing/attribution.js';
import { refreshCredits } from '../state/credits.js';
import { navigate } from '../router.js';
import { takeReturnTo } from '../state/auth.js';
import { toastApiError } from '../ui/toast.js';

export function renderVerify(root: HTMLElement, token: string | null): void {
  root.innerHTML = `
    <div class="login-wrap">
      <div class="stack login-card" style="align-items:center; text-align:center;">
        <div class="empty-icon" id="verify-icon">⏳</div>
        <h1 class="page-title" style="font-size: var(--fs-xl);" id="verify-title">Signing you in…</h1>
        <p class="muted" id="verify-copy">Verifying your sign-in link.</p>
      </div>
    </div>
  `;

  if (!token) {
    fail(root, 'Link not valid', 'This sign-in link is missing its token.');
    return;
  }

  // Hand over the anonymous guest session (if any) so uploads follow the user.
  const guestToken = loadTokens()?.accessToken;
  api
    .verifyMagicLink(token, guestToken, attributionPayload())
    .then((res) => {
      setUserAfterSignIn(res.user);
      void refreshCredits();
      navigate(takeReturnTo() ?? '/home');
    })
    .catch((err: unknown) => {
      toastApiError(err, 'This sign-in link is invalid or expired.');
      const [title, message] = failureCopy(err);
      fail(root, title, message);
    });
}

/** Name the real reason, so a server error is not mistaken for an old link. */
function failureCopy(err: unknown): [string, string] {
  const code = err instanceof ApiError ? err.code : '';
  if (code === 'expired_token') return ['Link expired', 'This sign-in link has expired. Request a fresh one.'];
  if (code === 'consumed_token') return ['Link already used', 'Each sign-in link works once. Request a fresh one.'];
  if (code === 'invalid_token') return ['Link not valid', 'This sign-in link is not valid. Request a fresh one.'];
  const status = err instanceof ApiError ? ` (HTTP ${err.status})` : '';
  return ['Sign-in failed', `We could not sign you in${status}. Please request a fresh link.`];
}

function fail(root: HTMLElement, titleText: string, message: string): void {
  const icon = root.querySelector('#verify-icon');
  const title = root.querySelector('#verify-title');
  const copy = root.querySelector('#verify-copy');
  if (icon) icon.textContent = '⚠️';
  if (title) title.textContent = titleText;
  if (copy) copy.textContent = message;
  const btn = document.createElement('a');
  btn.className = 'btn btn-primary btn-block';
  btn.href = '#/login';
  btn.textContent = 'Back to sign in';
  root.querySelector('.login-card')?.appendChild(btn);
}
